/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */
import React, { useEffect, useRef, useState } from "react";
import { isArrayNotEmpty } from "cms/utils/empty-exists";
import { CardsItem } from "./cards-item";
import { ReactComponent as LeftArrow } from "cms/assets/icons/left-arrow-alt.svg";
import { ReactComponent as RightArrow } from "cms/assets/icons/right-arrow-alt.svg";
import { Button } from "@mui/material";
import { useWindowSize } from "layout/homepage/useWindowSize";

const CardsComponent = (props) => {

    const { component } = props;
    const { card_list: cardList, sectionName, subtitle, title } = component || {};

    const cardListRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const { width: screenWidth } = useWindowSize();

    const mobileBreakpoint = 480;
    const isDesktop = screenWidth > mobileBreakpoint;
    const maxScreenSize = 1600;
    const cardsItemWidthDesktop = 416;
    const cardsItemWidthMobile = 300;
    const cardsItemWidth = isDesktop ? cardsItemWidthDesktop : cardsItemWidthMobile;
    const cardsItemGap = isDesktop ? 32 : 24;
    const totalCards = cardList?.length || 0;
    const cardsContainerPadding = isDesktop ? 128 : 40;

    const cardsContainerWidth = Math.min(screenWidth, maxScreenSize) - cardsContainerPadding;
    const cardsPerPage = Math.max(1, Math.floor(cardsContainerWidth / (cardsItemWidth + cardsItemGap)));
    const scrollAmount = (cardsItemWidth + cardsItemGap) * cardsPerPage;

    const canScrollRight = currentPage < totalPages - 1;
    const canScrollLeft = currentPage > 0;
    const showPageControls = totalPages > 1;

    const cardsItemStyle = {
        minWidth: `${cardsItemWidth}px`,
        width: `${cardsItemWidth}px`
    };

    const [scrollLeft, setScrollLeft] = useState(0);
    
    useEffect(() => {
      const handleScroll = () => {
        setScrollLeft(cardListRef.current?.scrollLeft);
      };
      const node = cardListRef.current;
      if (node) {
        node.addEventListener("scroll", handleScroll);
      }
      return () => node?.removeEventListener("scroll", handleScroll);
    }, []);


    useEffect(() => {
        const pages = Math.ceil(totalCards / cardsPerPage);
        setTotalPages(pages);
    }, [totalCards, cardsPerPage]);

    const scrollToCard = (cardIndex) => {
        if (cardListRef.current) {
            const targetScrollLeft = cardIndex * (cardsItemWidth + cardsItemGap);
            cardListRef.current.scrollTo( { left: targetScrollLeft, behavior: "smooth" });
        }
    };

    const scrollToPage = (pageIndex) => {
        scrollToCard(pageIndex * cardsPerPage);
    };

    useEffect(() => {
        let pageIndex = Math.ceil(scrollLeft / scrollAmount);
        pageIndex = Math.max(0, pageIndex);
        pageIndex = Math.min(pageIndex, totalPages - 1);
        setCurrentPage(pageIndex);
    },[scrollLeft, scrollAmount, totalPages]);

    return (
        <div className={"cards-component"}>
            {sectionName && <h4>{sectionName}</h4>}
            {title && <h2>{title}{subtitle && <span>{` ${subtitle}`}</span>}</h2>}

            {isArrayNotEmpty(cardList) &&
                <div className='card-list-container'>
                    <div className='card-list-items' ref={cardListRef} style={{
                        gap: cardsItemGap
                    }}>
                        {cardList.map((item, index) => (
                            <CardsItem
                                key={index}
                                item={item}
                                style={cardsItemStyle}
                            />
                        ))}
                    </div>
                        <div className={`card-list-controls ${!showPageControls ? "hide" : ""}`}>
                            <div className='card-list-dots'>
                                {Array.from({ length: totalPages }).map((_, index) => (
                                    <span
                                        key={`card-list-dot-${index}`}
                                        className={`card-list-dot ${index === currentPage ? "active" : ""}`}
                                    />
                                ))}
                            </div>
                            <div className='card-list-arrows'>
                                <Button
                                    className={`scroll-arrow ${!canScrollLeft ? "disabled " : ""}`}
                                    disabled={!canScrollLeft}
                                    onClick={() => scrollToPage(currentPage - 1)}
                                >
                                    <LeftArrow alt='left-arrow' />
                                </Button>
                                <Button
                                    className={`scroll-arrow ${!canScrollRight ? "disabled " : ""}`}
                                    disabled={!canScrollRight}
                                    onClick={() => scrollToPage(currentPage + 1)}
                                >
                                    <RightArrow alt='right-arrow' />
                                </Button>
                            </div>
                        </div>
                </div>
            }
        </div>
    );
};

CardsComponent.displayName = "CardsComponent";
export { CardsComponent };
